import Head from "next/head";
import Link from "next/link";
import { Box, Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    group: "How It Works",
    answer: (
      <>
        <b>Xavier Pass</b> - Includes an assigned seat in the Upper View Level.
        Pass holders do have the ability to upgrade to an assigned seat at an
        additional cost, subject to availability.
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        The <b>Xavier Pass</b> is a mobile-only program available exclusively
        through the free MLB Ballpark app (required for ticket delivery). Your
        Ballpark App login must be tied in with your Xavier.edu email.
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        On the morning before each homestand, you will receive a text to opt-in
        for the games in that homestand. See FAQs for more information.
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        When you&apos;re ready to enter Great American Ball Park, access your
        passes in the MLB Ballpark app, scan in with your barcode and enjoy the
        game from your designated area!
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        <b>Please note:</b> All your pass tickets will be available via the MLB
        Ballpark app on your iPhone or Android phone. There are no
        refunds/exchanges. Tickets are non-transferable.{" "}
        <b>
          Xavier Pass tickets may not be resold or offered for resale to third
          parties.
        </b>{" "}
        Any attempted resale of these tickets may result in the deactivation of
        such tickets; holders of deactivated tickets will not be permitted to
        enter the ballpark. Deactivated tickets will be refunded to the original
        form of payment, minus applicable service fees.
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        <b>
          If the Reds determine, in their sole discretion, that any ticket buyer
          purchases tickets for the primary purpose of reselling those tickets,
          then those purchases will be canceled without notice.
        </b>
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "What is the Xavier Pass?",
    answer: (
      <>
        The Xavier Pass gives you the ability to attend all Reds home games from{" "}
        <b>March 27th through May 13th</b>!
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "When is the check-in window?",
    answer: (
      <>
        The check-in window opens at <b>9am</b> on the first day of each
        homestand. You will be notified via text when the period is open.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Does the Xavier Pass include an assigned seat?",
    answer: (
      <>
        The Pass will typically include an assigned seat in the Upper View
        Level. If Upper View Level seating is not available The Reds will
        provide an alternative seating assignment. Pass holders do have the
        ability to upgrade seats at an additional cost, subject to availability.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Can we be seated together if our passes were purchased under different orders?",
    answer:
      "Yes! Simply create a group in your account and add the friends or family you want to sit with—just make sure they have purchased a pass as well.",
  },
  {
    group: "Frequently Asked Questions",
    question: "How do I upgrade my seats?",
    answer:
      "Open the MLB Ballpark App and click on the Reds tab at the bottom of the screen. Select the Seat Upgrade tile. Select your new seating location and enter the number of tickets you'd like to upgrade. Add payment and check out. The upgraded tickets will be added to the ticket tab of your MLB Ballpark App. Note: Upgrades are available a few hours before game time and are subject to availability.",
  },
  {
    group: "Frequently Asked Questions",
    question: "When do I need to request guest tickets?",
    answer: (
      <>
        Guest tickets need to be requested no later than four hours before
        scheduled first pitch.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Where are my tickets delivered?",
    answer:
      "All of your tickets will be delivered digitally via the MLB Ballpark app.",
  },
  {
    group: "Frequently Asked Questions",
    question: "When will my tickets appear in my MLB Ballpark App?",
    answer: (
      <>
        Tickets will be delivered to your MLB Ballpark App account 3 hours prior
        to first pitch.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Are there any penalties if I do not opt in?",
    answer: (
      <>
        There are no penalties for not attending a game, however you do need to
        confirm your attendance (opt-in) to receive a ticket.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Can the Xavier Pass tickets be transferred to a friend?",
    answer: (
      <>
        No, Xavier Passes are non-transferable and are only available on the
        phone of the Xavier Pass purchaser. Xavier Passes, guest passes, and
        upgraded tickets cannot be resold. Violators will be removed from the
        program.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Can I sell my Xavier Pass for games that I do not attend?",
    answer: (
      <>
        No, Xavier Passes are non-transferable and are only available on the
        phone of the Xavier Pass purchaser. Xavier Passes, guest passes, and
        upgraded tickets cannot be resold. Violators will be removed from the
        program.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Can I use a different email than my Xavier email?",
    answer:
      "No, offer is only valid to the email registered under your Xavier.edu email.",
  },
  {
    group: "Frequently Asked Questions",
    question: "What happens if a game time changes?",
    answer:
      "All game times are subject to change. Your Pass ticket will be valid only for the date of the game and is non-exchangeable.",
  },
  {
    group: "Frequently Asked Questions",
    question: "What if one of my games is rained out or postponed?",
    answer:
      "If a game is postponed your Xavier Pass ticket will be valid for the rescheduled game only.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Will I be considered a Season Ticket Member? Do I have access to Season Ticket Member events?",
    answer:
      "No, Xavier Pass holders are not eligible for Season Ticket Member benefits. Tickets are not transferable and cannot be exchanged for a later date.",
  },
  {
    group: "Frequently Asked Questions",
    question: "Does the Xavier Pass include postseason access?",
    answer:
      "No, the Xavier Pass is not considered a season plan and does not guarantee any postseason access.",
  },
  {
    group: "Frequently Asked Questions",
    question: "Are any other events included with the Xavier Pass?",
    answer:
      "The Xavier Pass is only valid for regular-season home games. It does not include tickets to non-game events at Great American Ball Park.",
  },
  {
    group: "Frequently Asked Questions",
    question: "How can I get a refund and cancel my Xavier Pass?",
    answer: (
      <>
        By selecting the Place Order button, you agree to purchase the tickets
        you have selected. No Refunds. No Exchanges. Please visit{" "}
        <Link
          target="_blank"
          href="https://www.mlb.com/reds/tickets/info/terms-conditions"
        >
          Reds.com/terms
        </Link>{" "}
        for more information. You can also contact us at{" "}
        <Link href="mailto:ticketservices@reds.com">
          ticketservices@reds.com
        </Link>
        .
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Why does the log-in page look different when I'm redirected to the buying page?",
    answer:
      "The MLB log-in page may look different, but it's the same log-in process. You can use your MLB account to sign in.",
  },
  {
    group: "Frequently Asked Questions",
    question: "Is there a deadline to purchase the pass?",
    answer:
      "Passes are available on a limited inventory basis and may sell out at any time. We encourage fans to purchase early, as availability is not guaranteed. While supplies last.",
  },
  {
    group: "Frequently Asked Questions",
    question: "Where can I find a full list of terms and conditions?",
    answer: (
      <>
        All Reds game tickets, including the Xavier Pass, are subject to the
        terms and conditions listed at{" "}
        <Link href="https://www.reds.com/terms" target="_blank">
          reds.com/terms
        </Link>
        .
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    By clicking &quot;Purchase Pass&quot;, you accept the{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/reds/tickets/info/terms-conditions"
    >
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/official-information/terms-of-use"
    >
      MLB Terms of Use
    </Link>
    , as well as policies applicable to use.
    <br />
    <br />
    In connection with your purchase, you will receive ticket-related commercial
    emails from{" "}
    <Link target="_blank" href="https://www.mlb.com/reds">
      Official Cincinnati Reds Website | MLB.com
    </Link>
    . You may unsubscribe at any time.
    <br />
    <br />
    If you purchase tickets, you may receive customer service messages via
    e-mail and text message from the Cincinnati Reds, including optional surveys
    regarding your ballpark experience.
    <br />
    <br />
    Clicking Purchase Pass will charge your credit card and your purchase will
    be complete. All sales are final. There are no refunds or exchanges.
    <br />
    <br />
    For more information regarding your purchase, please visit{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/reds/tickets/info/terms-conditions"
    >
      Terms &amp; Conditions
    </Link>
    .
    <br />
    <br />
    The terms and conditions of your ticket and policies applicable to use are
    updated to address COVID-19, health &amp; safety. By clicking Buy Tickets,
    you accept these{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/reds/tickets/info/terms-conditions"
    >
      Terms and Conditions
    </Link>{" "}
    as well as policies applicable to use.
    <br />
    <br />
    For a full list of{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/reds/tickets/info/terms-conditions"
    >
      Terms &amp; Conditions
    </Link>{" "}
    please visit{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/reds/tickets/info/terms-conditions?partnerId=redirect-cin-terms"
    >
      Ticket Terms &amp; Conditions | Cincinnati Reds
    </Link>{" "}
    and{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/official-information/terms-of-use"
    >
      MLB Terms of Use.
    </Link>
    <br />
    <br />
    <b>
      The Cincinnati Reds reserve the right not to sell to, and to cancel any
      purchase made by, individuals or companies whose intent is to resell
      tickets.
    </b>
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/reds/tickets/info/terms-conditions?partnerId=redirect-cin-terms"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        General TicketingTerms & Conditions | Cincinnati Reds
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-tik-tok-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !(function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CBP9R33C77UB6N07V05G");
                ttq.page();
                console.log('tiktok pixel page view fired.');
              })(window, document, "ttq");

              window.addEventListener("${constants.customEvents.purchaseSuccess}", async function(e) {
                const { cartRes, checkoutRes, passes, user } = e.detail;

                ttq.identify({
                  email: user.email,
                  phone_number: "+1" + user.phoneNumber,
                });

                if(cartRes.products && cartRes.products.length) {
                  for(let product of cartRes.products){
                    ttq.track("CompletePayment", {
                      content_id: product.eventCode,
                      content_name: product.productTitle,
                      quantity: String(product.seatCount),
                      price: String(product.price),
                      value: String(product.price * product.seatCount),
                      currency: "USD",
                    });
                    await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                  }
                };

                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_id = passDetail.eventCode;
                  content_name = passDetail.passTitle;
                  price = String(passDetail.price);
                  quantity = String(cartRes.passes[0].seatCount);

                  ttq.track("CompletePayment", {
                    content_id: passDetail.eventCode,
                    content_name: passDetail.passTitle,
                    quantity: String(cartRes.passes[0].seatCount),
                    price: String(passDetail.price),
                    value: String(cartRes.totalAmount),
                    currency: "USD",
                  });
                };
                console.log('tik tok pixel purchase event fired.');
              });
            `,
        }}
      />

      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '368362963346231');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=368362963346231&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const HomePageZeroStateDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      You can purchase one{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.landing,
          query: { teamSchema },
        }}
      >
        here
      </Link>
      , or check your{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.purchase_history,
          query: { teamSchema },
        }}
      >
        Purchase History
      </Link>{" "}
      to see passes you’ve gifted.
    </Box>
  );
};

export const RedeemGiftSuccessModalDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      Your gift is all set! Create a{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.my_group,
          query: { teamSchema },
        }}
      >
        group
      </Link>{" "}
      to bring friends along, or explore the schedule in{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.home,
          query: { teamSchema },
        }}
      >
        Upcoming Games
      </Link>
      . We’ll send you a text before each homestand to opt in to games.
    </Box>
  );
};
